import { create } from 'zustand';
import type { ChatMessage } from '../../hooks/use-chat-ws';
import { getFrameAgent, getParentToolUseId, getSpawnSubagentType, isSubagentSpawn, isTerminalTaskStatus, journeyDisplay, journeyGroupStatus } from '../../utils/journey-attribution';

// A delegated background sub-agent, surfaced in the Background-agents sidebar. One per spawn
// (a Task/Agent tool_use); its live status + usage come from the frames attributed to that spawn.
export type BackgroundAgent = {
  /** The spawn tool_use id. */
  id: string;
  /** subagent_type (e.g. 'agent-runner', 'general-purpose'). */
  agent: string;
  /** Curated display name (journeyDisplay). */
  label: string;
  /** Latest lifecycle status: '' | started | running | completed | failed | stopped | killed. */
  status: string;
  running: boolean;
  /** Cumulative usage off the latest task_progress frame (0 when the backend hasn't reported it). */
  toolUses: number;
  durationMs: number;
  /** The task the sub-agent was given (the spawn's prompt), shown atop its transcript. */
  prompt: string;
  /** The sub-agent's own frames (attributed via parent_tool_use_id) — its transcript. */
  frames: ChatMessage[];
};
export type BackgroundAgents = {
  items: BackgroundAgent[];
};
const EMPTY_AGENTS: BackgroundAgents = {
  items: []
};
type Usage = {
  tool_uses?: number;
  duration_ms?: number;
};
const readUsage = (msg: ChatMessage): Usage | undefined => {
  if (msg.type !== 'task_progress') return undefined;
  const usage = (msg.data as {
    usage?: unknown;
  } | undefined)?.usage;
  return usage && typeof usage === 'object' ? usage as Usage : undefined;
};

// The task handed to the sub-agent, off the spawn's Task/Agent tool input.
const getSpawnPrompt = (msg: ChatMessage): string => {
  const input = (msg as {
    input?: {
      prompt?: unknown;
      description?: unknown;
    };
  }).input;
  const prompt = input?.prompt ?? input?.description;
  return typeof prompt === 'string' ? prompt : '';
};

// Roll the message stream into one entry per delegated spawn: register spawns in order, then fold
// each frame into its parent spawn (attribution via getParentToolUseId). Status is the spawn's
// latest lifecycle status; token/tool-use/duration come from the latest usage-bearing frame.
export const deriveBackgroundAgents = (messages: ChatMessage[]): BackgroundAgents => {
  const order: string[] = [];
  const framesBySpawn = new Map<string, ChatMessage[]>();
  const subagentType = new Map<string, string>();
  const promptBySpawn = new Map<string, string>();
  for (const message of messages) {
    if (!isSubagentSpawn(message)) continue;
    const id = (message as {
      id: string;
    }).id;
    if (!framesBySpawn.has(id)) {
      framesBySpawn.set(id, []);
      subagentType.set(id, getSpawnSubagentType(message));
      promptBySpawn.set(id, getSpawnPrompt(message));
      order.push(id);
    }
  }
  for (const message of messages) {
    const parent = getParentToolUseId(message);
    const frames = parent ? framesBySpawn.get(parent) : undefined;
    if (frames) frames.push(message);
  }
  const items = order.map(id => {
    const frames = framesBySpawn.get(id) ?? [];
    let agent = subagentType.get(id) ?? '';
    let toolUses = 0;
    let durationMs = 0;
    for (const frame of frames) {
      if (!agent) agent = getFrameAgent(frame);
      const usage = readUsage(frame);
      if (usage) {
        if (typeof usage.tool_uses === 'number') toolUses = usage.tool_uses;
        if (typeof usage.duration_ms === 'number') durationMs = usage.duration_ms;
      }
    }
    // Fallback when the backend omits usage — derived from the frames (not Date.now) so replay stays stable.
    if (toolUses === 0) {
      toolUses = new Set(frames.filter(f => f.type === 'tool_use').map(f => (f as {
        id?: string;
      }).id).filter(Boolean)).size;
    }
    if (durationMs === 0) {
      const times = frames.map(f => Date.parse((f as {
        timestamp?: string;
      }).timestamp ?? '')).filter(t => !Number.isNaN(t));
      if (times.length >= 2) durationMs = Math.max(...times) - Math.min(...times);
    }
    const status = journeyGroupStatus(frames);
    return {
      id,
      agent,
      label: journeyDisplay(agent).label,
      status,
      running: !isTerminalTaskStatus(status),
      toolUses,
      durationMs,
      prompt: promptBySpawn.get(id) ?? '',
      frames
    };
  });
  return {
    items
  };
};

// Bridges the background-agents list (derived inside ChatSession from its message stream) to the
// sidebar, which renders as a sibling of the chat. One global widget ⇒ app-global module store, like
// chat-resources-context: ChatSession publishes via setAgents; the panel reads.
type BackgroundAgentsStore = {
  agents: BackgroundAgents;
  setAgents: (agents: BackgroundAgents) => void;
};
const useStore = create<BackgroundAgentsStore>(set => ({
  agents: EMPTY_AGENTS,
  setAgents: agents => set({
    agents
  })
}));
export const useBackgroundAgents = () => {
  return useStore(_temp);
};
export const useSetBackgroundAgents = () => {
  return useStore(_temp2);
};
function _temp(state) {
  return state.agents;
}
function _temp2(state) {
  return state.setAgents;
}